import { useForm, Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { useBasePath } from '@/lib/paths'
import { formatCurrency } from '@/lib/utils'
import { CreditCard, CheckCircle2, Clock, Shield } from 'lucide-react'

type License = {
  id: string
  type_name: string
}

type Record = {
  id: string
  period_start_date: string
  period_end_date: string
}

type Payment = {
  id: string
  gov_fee_cents: number
  service_fee_cents: number
  total_cents: number
  currency: string
  paid: boolean
  paid_at: string | null
  invoice?: {
    number: string
    issued_at: string
  }
  receipt?: {
    number: string
    issued_at: string
    payment_method: string
  }
} | null

type Type = {
  gov_fee_cents: number
  service_fee_cents: number
  total_cents: number
}

type Props = {
  license: License
  record: Record
  payment: Payment
  type: Type
}

export default function PaymentShow({ license, record, payment, type }: Props) {
  const basePath = useBasePath()
  const form = useForm({})
  const paymentCancelled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'cancel'

  return (
    <DashboardLayout
      title="Payment"
      breadcrumbs={[
        { label: 'Licenses', href: 'licenses' },
        { label: license.type_name, href: `licenses/${license.id}` },
        { label: `${record.period_start_date} - ${record.period_end_date}`, href: `licenses/${license.id}/records/${record.id}` },
        { label: 'Payment' },
      ]}
    >
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">Payment</h1>
          <p className="text-sm text-muted-foreground">Pay for your license</p>
        </div>

        {paymentCancelled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-700 font-medium">Payment cancelled</p>
            <p className="text-xs text-amber-600 mt-1">You can try again when you're ready.</p>
          </div>
        )}

        {!payment ? (
          <div className="bg-muted rounded-xl p-5 space-y-5">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service Fee</span>
                <span className="text-foreground">{formatCurrency(type.service_fee_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Government Fee</span>
                <span className="text-foreground">{formatCurrency(type.gov_fee_cents)}</span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">Total</span>
                <span className="text-foreground text-lg">{formatCurrency(type.total_cents)}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-border/60">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Secure Payment</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Your payment is processed securely</p>
                </div>
              </div>
            </div>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/licenses/${license.id}/records/${record.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Processing...' : `Pay ${formatCurrency(type.total_cents)}`}
              </Button>
            </form>
          </div>
        ) : payment.paid ? (
          <div className="bg-green-50 rounded-xl p-5 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Payment Complete</h2>
                <p className="text-xs text-muted-foreground">
                  {payment.paid_at && `Paid on ${new Date(payment.paid_at).toLocaleDateString()}`}
                  {payment.receipt && ` · Receipt: ${payment.receipt.number}`}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service Fee</span>
                <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Government Fee</span>
                <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
              </div>
              <div className="h-px bg-green-200" />
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">Total Paid</span>
                <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
              </div>
            </div>

            <Button asChild className="w-full bg-green-600 hover:bg-green-700">
              <Link href={`${basePath}/licenses/${license.id}/records/${record.id}`}>Back to Record</Link>
            </Button>
          </div>
        ) : (
          <div className="bg-muted rounded-xl p-5 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Payment Pending</h2>
                <p className="text-xs text-muted-foreground">
                  Complete checkout to proceed
                  {payment.invoice && ` · Invoice: ${payment.invoice.number}`}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service Fee</span>
                <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Government Fee</span>
                <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">Total</span>
                <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
              </div>
            </div>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/licenses/${license.id}/records/${record.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Redirecting...' : `Pay ${formatCurrency(payment.total_cents)}`}
              </Button>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
